import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const TYPE_META = {
  bug:         { label: 'Bug',             color: '#EC2124', icon: '🐛' },
  feature:     { label: 'Feature',         color: '#46BB89', icon: '💡' },
  ux:          { label: 'UX',              color: '#a78bfa', icon: '✏️' },
  performance: { label: 'Performance',     color: '#f59e0b', icon: '⚡' },
  question:    { label: 'Question',        color: '#60a5fa', icon: '❓' },
  other:       { label: 'Other',           color: '#94a3b8', icon: '📝' },
};

const STATUS_META = {
  open:        { label: 'Open',        color: '#60a5fa' },
  triaged:     { label: 'Triaged',     color: '#a78bfa' },
  in_progress: { label: 'In Progress', color: '#f59e0b' },
  resolved:    { label: 'Resolved',    color: '#46BB89' },
  wont_fix:    { label: "Won't Fix",   color: '#94a3b8' },
};

const SEVERITY_COLOR = {
  low:      '#94a3b8',
  medium:   '#60a5fa',
  high:     '#f59e0b',
  critical: '#EC2124',
};

const PRIORITY_COLOR = {
  low:      '#94a3b8',
  medium:   '#60a5fa',
  high:     '#f59e0b',
  critical: '#EC2124',
};

export default function FeedbackDashboard() {
  const { getToken } = useAuth();
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterType,   setFilterType]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ limit: 200 });
      if (filterStatus) params.set('status', filterStatus);
      if (filterType)   params.set('type',   filterType);
      const res = await fetch(`${API_BASE}/feedback?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) { setItems(data.items); setTotal(data.total); }
    } catch {} finally { setLoading(false); }
  }, [getToken, filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/feedback/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    const data = await res.json();
    if (data.ok) setItems(prev => prev.map(i => i._id === id ? data.item : i));
  };

  const cell = { padding: '10px 12px', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'top' };
  const th   = { ...cell, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#0f1115' };

  return (
    <div style={{ minHeight: '100vh', background: '#0f1115', color: '#f1f5f9', padding: '32px 24px' }}>
      <div style={{ maxWidth: 1300, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>💬 Feedback</h1>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
              {total} submission{total !== 1 ? 's' : ''} total
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Status filter */}
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.12)', color: '#f1f5f9', borderRadius: 7, padding: '6px 10px', fontSize: 12 }}
            >
              <option value="">All statuses</option>
              {Object.entries(STATUS_META).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </select>
            {/* Type filter */}
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.12)', color: '#f1f5f9', borderRadius: 7, padding: '6px 10px', fontSize: 12 }}
            >
              <option value="">All types</option>
              {Object.entries(TYPE_META).map(([v, m]) => (
                <option key={v} value={v}>{m.icon} {m.label}</option>
              ))}
            </select>
            <button onClick={load} style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8', borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>
              Refresh
            </button>
          </div>
        </div>

        {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>}

        {!loading && items.length === 0 && (
          <div style={{ color: '#64748b', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
            No feedback yet. The 💬 button is visible to all service users on every page.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Type</th>
                  <th style={th}>Title</th>
                  <th style={th}>Priority</th>
                  <th style={th}>AI Severity</th>
                  <th style={th}>Affected Area</th>
                  <th style={th}>Submitted by</th>
                  <th style={th}>Page</th>
                  <th style={th}>Status</th>
                  <th style={th}>GitHub</th>
                  <th style={th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const tm = TYPE_META[item.type] || TYPE_META.other;
                  const sm = STATUS_META[item.status] || STATUS_META.open;
                  const isExpanded = expanded === item._id;
                  return (
                    <React.Fragment key={item._id}>
                      <tr
                        onClick={() => setExpanded(isExpanded ? null : item._id)}
                        style={{ cursor: 'pointer', background: isExpanded ? 'rgba(255,255,255,0.03)' : 'transparent' }}
                        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={cell}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: tm.color, background: tm.color + '18', borderRadius: 4, padding: '2px 7px' }}>
                            {tm.icon} {tm.label}
                          </span>
                        </td>
                        <td style={{ ...cell, maxWidth: 260 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                          {item.ai?.summary && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.ai.summary}
                            </div>
                          )}
                        </td>
                        <td style={cell}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: PRIORITY_COLOR[item.priority] || '#94a3b8' }}>
                            {item.priority || '—'}
                          </span>
                        </td>
                        <td style={cell}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: SEVERITY_COLOR[item.ai?.severity] || '#64748b' }}>
                            {item.ai?.severity || '—'}
                          </span>
                        </td>
                        <td style={{ ...cell, fontFamily: 'monospace', fontSize: 11, color: '#94a3b8' }}>
                          {item.ai?.affectedArea || '—'}
                        </td>
                        <td style={cell}>
                          <div style={{ fontSize: 12 }}>{item.userName || '—'}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{item.userRole || ''}</div>
                        </td>
                        <td style={{ ...cell, fontFamily: 'monospace', fontSize: 10, color: '#64748b', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.pageUrl || '—'}
                        </td>
                        <td style={cell} onClick={e => e.stopPropagation()}>
                          <select
                            value={item.status}
                            onChange={e => updateStatus(item._id, e.target.value)}
                            style={{
                              background: sm.color + '18',
                              border: `1px solid ${sm.color}44`,
                              color: sm.color,
                              borderRadius: 6, padding: '3px 7px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            }}
                          >
                            {Object.entries(STATUS_META).map(([v, m]) => (
                              <option key={v} value={v}>{m.label}</option>
                            ))}
                          </select>
                        </td>
                        <td style={cell} onClick={e => e.stopPropagation()}>
                          {item.githubIssueUrl ? (
                            <a href={item.githubIssueUrl} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11, color: '#60a5fa', textDecoration: 'none' }}>
                              #{item.githubIssueNumber} ↗
                            </a>
                          ) : (
                            <span style={{ fontSize: 11, color: '#334155' }}>—</span>
                          )}
                          {item.githubPrUrl && (
                            <div>
                              <a href={item.githubPrUrl} target="_blank" rel="noreferrer"
                                style={{ fontSize: 10, color: '#46BB89', textDecoration: 'none' }}>
                                PR ↗
                              </a>
                            </div>
                          )}
                        </td>
                        <td style={{ ...cell, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {new Date(item.createdAt).toLocaleDateString()}
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={10} style={{ padding: '0 16px 16px', background: 'rgba(255,255,255,0.02)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, paddingTop: 12 }}>
                              {/* Description */}
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Description</div>
                                <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                  {item.description || <span style={{ fontStyle: 'italic' }}>No description provided.</span>}
                                </div>
                              </div>
                              {/* AI Analysis */}
                              {item.ai?.summary && (
                                <div>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>AI Analysis</div>
                                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                                    <div><span style={{ color: '#64748b' }}>Summary:</span> {item.ai.summary}</div>
                                    {item.ai.suggestedFix && <div style={{ marginTop: 4 }}><span style={{ color: '#64748b' }}>Suggested fix:</span> {item.ai.suggestedFix}</div>}
                                    {item.ai.labels?.length > 0 && (
                                      <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                        {item.ai.labels.map(l => (
                                          <span key={l} style={{ fontSize: 10, background: 'rgba(255,255,255,0.07)', borderRadius: 4, padding: '2px 7px', color: '#94a3b8' }}>
                                            {l}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            {item.images?.length > 0 && (
                              <div style={{ marginTop: 14 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Screenshots</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                  {item.images.map((src, i) => (
                                    <a key={i} href={src} target="_blank" rel="noreferrer">
                                      <img
                                        src={src}
                                        alt={`screenshot ${i + 1}`}
                                        style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)' }}
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
